// skeleton\app\src\components\P4P\ProviderProfile\CommentsSidebar.tsx

import React, { useEffect, useRef, useState } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { useAuthContext } from "../../../Context/AuthContext";
import { ApiEndpoints } from "../../../constants/appConstants";
import httpService from "../../../services/httpService";
import { Comment } from "./models";
import { formatToLocalDateTime } from "../../../services/dateTimeService";

interface CommentsSidebarProps {
  sidebarVisible: boolean;
  setSidebarVisible: (newValue: boolean) => void;
  commentMeasureNumber: string;
  commentProviderMPI: string;
  commentLevel: string;
  commentMeasureId: number;
  updatePerformanceAreasForComments: (measureId: number) => void;
  providerAgencyName: string;
}

export default function CommentsSidebar({
  sidebarVisible,
  setSidebarVisible,
  commentMeasureNumber,
  commentProviderMPI,
  commentLevel,
  commentMeasureId,
  updatePerformanceAreasForComments,
  providerAgencyName,
}: CommentsSidebarProps) {
  const [currentComment, setCurrentComment] = useState("");
  const [commentData, setCommentData] = useState<Comment[]>([]);
  const { appUser } = useAuthContext();
  const lastCommentRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);

  // Not allowed: < > { } # * '
  const INVALID_COMMENT_CHARS = /[<>#{}*']/;

  const normalizedComment = (currentComment ?? "").trim();
  const hasInvalidChars = INVALID_COMMENT_CHARS.test(normalizedComment);
  const canSave = normalizedComment.length > 0 && !hasInvalidChars && !loading;

  const saveComment = () => {
    const payloadComment = (currentComment ?? "").trim();
    if (!payloadComment || INVALID_COMMENT_CHARS.test(payloadComment)) {
      return;
    }

    setLoading(true);
    httpService
      .post(ApiEndpoints.SavePfpComment, {
        commentAddedBy: appUser?.name,
        commentDescription: payloadComment,
        commentLevel: commentLevel,
        providerMPI: commentProviderMPI,
        measureId: commentMeasureId,
      })
      .then((response: any) => {
        if (response.status === 200) {
          setCurrentComment("");
          getAllComments();
          if (commentLevel == "measure") {
            updatePerformanceAreasForComments(commentMeasureId);
          }
        }
      })
      .catch((error: any) => {
        console.log("SaveComment response:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getAllComments = () => {
    setLoading(true);
    remember: httpService
      .post(ApiEndpoints.GetAllPfpComments, {
        commentLevel: commentLevel,
        providerMPI: commentProviderMPI,
        measureId: commentMeasureId,
      })
      .then((response: any) => {
        if (response.status === 200) {
          setCommentData(response.data);
        }
      })
      .catch((error: any) => {
        console.log("Get Comment response:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastCommentRef.current) {
      lastCommentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [commentData]);

  let commentSection: React.JSX.Element[] | React.JSX.Element = commentData?.map((comment, index) => {
    const formattedDateTime = formatToLocalDateTime(comment.createdDate);

    return (
      <div className="comment-container" ref={index === commentData.length - 1 ? lastCommentRef : null}>
        <div className="comment-header">
          <div className="comment-username">{comment.commentAddedBy}</div>
          <div className="comment-date">{formattedDateTime}</div>
        </div>
        <div className="comment">{comment.commentDescription}</div>
      </div>
    );
  });

  if (!commentData.length) {
    commentSection = loading ? (
      <span className="comments-loader">Loading...</span>
    ) : (
      <span className="no-comments-found">No Comments Found</span>
    );
  }

  return (
    <div className="right-sidebar">
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        position="right"
        style={{ width: "28rem" }}
        showCloseIcon={false}
      >
        <div className="comments-sidebar-header">
          <h3>
            Comments for {commentMeasureNumber} {`(${providerAgencyName})`}
          </h3>
          <Button
            onClick={() => setSidebarVisible(false)}
            icon="pi pi-times"
            size="small"
            rounded
            outlined
            severity="secondary"
            aria-label="Cancel"
            className="comments-close-button"
          />
        </div>

        <div className="comments-sidebar-content">
          <div className="comments">{commentSection}</div>

          <div className="comments-form">
            <InputTextarea
              id="commentsInput"
              value={currentComment}
              rows={3}
              placeholder="Enter your comment here"
              onChange={(e: any) => setCurrentComment(e.target.value)}
            ></InputTextarea>

            {hasInvalidChars && (
              <div className="alert-message" style={{ color: "red", marginTop: "6px" }}>
                Invalid characters not allowed: &lt; &gt; {"{"} {"}"} # * '
              </div>
            )}

            <Button id="saveComment" disabled={!canSave} onClick={saveComment} loading={loading}>
              Save Comment
            </Button>
          </div>
        </div>
      </Sidebar>
    </div>
  );
}
